import {ApiResponse, PaginatedApiReponse} from '../models/ApiResponse';
import {
  LessonGenerationFromPromptModel,
  LessonGenerationModel,
} from '../models/AI/LessonGenerationModel';
import {apiClient} from './baseApiClient';
import {MicrolessonListItem} from '../models/Microlessons/MicrolessonListItem';
import {
  CreateMicrolessonModel,
  Microlesson,
} from '../models/Microlessons/Microlesson';
import {LessonPresentation} from '../models/Course/LessonPresentation';
import {MicrolessonGenerationCostModel} from '../models/Microlessons/MicrolessonGenerationCostModel';
import {MicrolessonGenerationCostResponse} from '../models/Microlessons/MicrolessonGenerationCostResponse';

class MicrolessonsApi {
  public async genrateLessonWithAI(
    organizationId: string,
    model: LessonGenerationModel,
  ): Promise<ApiResponse<any>> {
    return apiClient.post(
      `/microlessons/${organizationId}/generate_from_video`,
      model,
    );
  }

  public async generateLessonFromSlide(
    organizationId: string,
    model: {
      libraryMediaFileId: string;
      addBranching: boolean;
      addAssessment: boolean;
      passingScore?: number;
      language: string;
      dialect: string;
      gender: string;
      name: string;
      backgroundAudio?: string;
      backgroundAudioFileId?: string;
    },
  ): Promise<ApiResponse<any>> {
    return apiClient.post(
      `/microlessons/${organizationId}/generate_from_slide`,
      model,
    );
  }

  public async genrateLessonFromPromptWithAI(
    organizationId: string,
    model: LessonGenerationFromPromptModel,
  ): Promise<ApiResponse<any>> {
    return apiClient.post(
      `/microlessons/${organizationId}/generate_from_prompt`,
      model,
    );
  }

  public async getMicrolessonsList(
    organizationId: string,
    page: number,
    pageSize: number,
    search?: string,
  ): Promise<PaginatedApiReponse<MicrolessonListItem>> {
    return apiClient.post(`/microlessons/${organizationId}/list`, {
      page,
      pageSize,
      orderBy: '',
      search,
      orderAsk: false,
    });
  }

  public async getMicrolessonById(
    organizationId: string,
    lessonId: string,
  ): Promise<ApiResponse<Microlesson>> {
    return apiClient.get(`/microlessons/${organizationId}/${lessonId}`);
  }

  public async getMicrolessonPreview(
    organizationId: string,
    lessonId: string,
  ): Promise<ApiResponse<LessonPresentation>> {
    return apiClient.get(`/microlessons/${organizationId}/preview/${lessonId}`);
  }

  public async createMicrolesson(
    organizationId: string,
    model: CreateMicrolessonModel,
  ): Promise<ApiResponse<MicrolessonListItem>> {
    return apiClient.post(`/microlessons/${organizationId}/create`, model);
  }

  public async editMicrolesson(
    organizationId: string,
    lessonId: string,
    model: CreateMicrolessonModel,
  ): Promise<ApiResponse<any>> {
    return apiClient.post(
      `/microlessons/${organizationId}/edit/${lessonId}`,
      model,
    );
  }

  public async uploadMicrolessonAvatar(
    organizationId: string,
    lessonId: string,
    avatar: File,
  ) {
    const form = new FormData();
    form.append('poster', avatar);
    form.append('lessonId', lessonId);
    return apiClient.post(`/microlessons/${organizationId}/poster`, form);
  }

  public async getMicrolessonsLibrary(
    organizationId: string,
    page: number,
    pageSize: number,
    isGradable?: boolean,
    search?: string,
  ): Promise<PaginatedApiReponse<Microlesson>> {
    return apiClient.post(`/microlessons/${organizationId}/library`, {
      page,
      pageSize,
      search,
      isGradable,
      orderAsk: false,
    });
  }

  public async copyMicrolessonToCourse(
    organizationId: string,
    courseId: string,
    folderId: string,
    microlessonId: string,
  ): Promise<ApiResponse<any>> {
    return apiClient.post(`/microlessons/${organizationId}/copy_to_course`, {
      courseId,
      folderId,
      microlessonId,
    });
  }

  public async deleteMicrolesson(
    organizationId: string,
    microlessonId: string,
  ): Promise<ApiResponse<any>> {
    return apiClient.delete(`/microlessons/${organizationId}/${microlessonId}`);
  }

  public async getMicrolessonCostEstimation(
    organizationId: string,
    model: MicrolessonGenerationCostModel,
  ): Promise<ApiResponse<MicrolessonGenerationCostResponse>> {
    return apiClient.post(`/Microlessons/${organizationId}/estimation`, model);
  }

  public async checkGenerationAvailability(
    organizationId: string,
  ): Promise<ApiResponse<boolean>> {
    const response = await apiClient.get<{isAvailable: boolean}>(
      `/Microlessons/${organizationId}/available_generation`,
    );

    return {
      success: true,
      data: response.data.isAvailable,
    };
  }
}

export default MicrolessonsApi;
